import type {
  Shape,
  WallPathShape,
  WallShape
} from "@gettactic/map-builder/src/types";
import { computeWallPathBounds } from "@gettactic/map-builder/src/utils/shapeFactory";
import type { Point } from "../types";
import { rotatePoint } from "./bounds";
import type { DocumentState } from "./operations";

/** Shapes whose size is their width/height box scale in place; spots and
 *  groups scale through scaleX/scaleY; walls and paths scale their points. */
const isBoxShape = (shape: Shape): boolean =>
  shape.type !== "spot" &&
  shape.type !== "wall" &&
  shape.type !== "wall-path" &&
  shape.type !== "group";

const scalePoint = (
  point: Point,
  anchor: Point,
  sx: number,
  sy: number
): Point => ({
  x: anchor.x + (point.x - anchor.x) * sx,
  y: anchor.y + (point.y - anchor.y) * sy
});

/** Scales a shape about a world anchor, as a multi-selection resize does. */
export const scaleShape = (
  shape: Shape,
  anchor: Point,
  sx: number,
  sy: number
): Shape => {
  if (shape.type === "wall") {
    const wall = shape as WallShape;
    const start = scalePoint({ x: wall.x, y: wall.y }, anchor, sx, sy);
    const end = scalePoint({ x: wall.x2, y: wall.y2 }, anchor, sx, sy);
    return {
      ...wall,
      x: Math.round(start.x),
      y: Math.round(start.y),
      x2: Math.round(end.x),
      y2: Math.round(end.y)
    };
  }
  if (shape.type === "wall-path") {
    const path = shape as WallPathShape;
    const vertices = path.vertices.map((vertex) => {
      const moved = scalePoint(vertex, anchor, sx, sy);
      return { ...vertex, x: Math.round(moved.x), y: Math.round(moved.y) };
    });
    return { ...path, vertices, ...computeWallPathBounds(vertices) };
  }
  const origin = scalePoint({ x: shape.x, y: shape.y }, anchor, sx, sy);
  if (!isBoxShape(shape)) {
    // A desk's drawing has a fixed layout, so it scales through its own
    // scale factors (which the viewer honours) rather than width/height.
    return {
      ...shape,
      x: Math.round(origin.x),
      y: Math.round(origin.y),
      scaleX: Math.round((shape.scaleX || 1) * sx * 100) / 100,
      scaleY: Math.round((shape.scaleY || 1) * sy * 100) / 100
    } as Shape;
  }
  const scaled = {
    ...shape,
    x: Math.round(origin.x),
    y: Math.round(origin.y),
    width: Math.max(5, Math.round(shape.width * sx)),
    height: Math.max(5, Math.round(shape.height * sy))
  } as Shape;
  if (scaled.type === "circle") {
    scaled.radius = Math.round(Math.max(scaled.width, scaled.height) / 2);
  }
  return scaled;
};

export interface ScaleFrame {
  /** World point the frame rotates about. */
  pivot: Point;
  rotation: number;
}

/** Scales a selection about `anchor`. With a rotated frame, `anchor` and
 *  the axes are in that frame: shapes are turned into it, scaled, and
 *  turned back, so a rotated selection grows along its own sides. */
export const scaleShapes = (
  document: DocumentState,
  ids: string[],
  anchor: Point,
  sx: number,
  sy: number,
  frame?: ScaleFrame
): DocumentState => {
  const shapes = { ...document.shapes };
  const rotation = frame?.rotation ?? 0;
  for (const id of ids) {
    const shape = shapes[id];
    if (!shape || shape.locked) continue;
    if (!frame || !rotation) {
      shapes[id] = scaleShape(shape, anchor, sx, sy);
      continue;
    }
    const inFrame = rotateShape(shape, frame.pivot, -rotation);
    const scaled = scaleShape(inFrame, anchor, sx, sy);
    shapes[id] = rotateShape(scaled, frame.pivot, rotation);
  }
  return { ...document, shapes };
};

/** Rigidly rotates a shape about a world centre: its origin swings around
 *  the centre and its own angle turns by the same amount. Walls and paths
 *  rotate their points instead; groups only swing. */
export const rotateShape = (
  shape: Shape,
  centre: Point,
  spin: number
): Shape => {
  if (shape.type === "wall") {
    const wall = shape as WallShape;
    const start = rotatePoint({ x: wall.x, y: wall.y }, centre, spin);
    const end = rotatePoint({ x: wall.x2, y: wall.y2 }, centre, spin);
    return {
      ...wall,
      x: Math.round(start.x),
      y: Math.round(start.y),
      x2: Math.round(end.x),
      y2: Math.round(end.y)
    };
  }
  if (shape.type === "wall-path") {
    const path = shape as WallPathShape;
    const vertices = path.vertices.map((vertex) => {
      const moved = rotatePoint(vertex, centre, spin);
      return { ...vertex, x: Math.round(moved.x), y: Math.round(moved.y) };
    });
    return { ...path, vertices, ...computeWallPathBounds(vertices) };
  }
  const origin = rotatePoint({ x: shape.x, y: shape.y }, centre, spin);
  const rotated = { ...shape, x: origin.x, y: origin.y } as Shape;
  if (shape.type !== "group") {
    rotated.rotation = ((shape.rotation || 0) + spin) % 360;
  }
  return rotated;
};

export const rotateShapes = (
  document: DocumentState,
  ids: string[],
  centre: Point,
  spin: number
): DocumentState => {
  const shapes = { ...document.shapes };
  for (const id of ids) {
    const shape = shapes[id];
    if (shape && !shape.locked) shapes[id] = rotateShape(shape, centre, spin);
  }
  return { ...document, shapes };
};
